import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { scrollToId, scrollToTop } from "@/lib/scroll";
import { trackClick } from "@/lib/api";
import { useContact } from "@/lib/contact";

const LINKS = [
    { label: "Home", target: "top" },
    { label: "Workspaces", target: "#gallery" },
    { label: "Amenities", target: "#amenities" },
    { label: "Pricing", target: "#pricing" },
    { label: "Contact", target: "#location" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const contact = useContact();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleLink = (link) => {
        setOpen(false);
        if (link.target === "top") return scrollToTop();
        scrollToId(link.target);
    };

    return (
        <>
            <motion.header
                initial={{ y: -70, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "border-b border-slate-200/80 bg-white/85 shadow-[0_8px_30px_-18px_rgba(22,50,79,0.35)] backdrop-blur-md"
                        : "bg-transparent"
                }`}
            >
                <div className="mx-auto flex h-[84px] max-w-7xl items-center justify-between px-5 sm:px-8">
                    <button
                        data-testid="navbar-logo"
                        onClick={scrollToTop}
                        aria-label="Work Island — back to top"
                        className="flex shrink-0 items-center"
                    >
                            <img
                                src="/assets/logo-full.png"
                                alt="Work Island — Co-Work · Connect · Create"
                                className="h-12 w-auto max-w-[58vw] object-contain object-left sm:h-16 sm:max-w-none"
                            />
                    </button>

                    <nav aria-label="Primary" className="hidden items-center gap-9 lg:flex">
                        {LINKS.map((link) => (
                            <button
                                key={link.label}
                                data-testid={`nav-link-${link.label.toLowerCase()}`}
                                onClick={() => handleLink(link)}
                                className="nav-link text-[15.5px] font-semibold tracking-wide text-ink/70 transition-colors duration-300 hover:text-ink"
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <a
                            data-testid="nav-call-link"
                            href={contact.telUrl}
                            onClick={() => trackClick("call", "navbar")}
                            className="btn-gradient group hidden items-center gap-2 rounded-full px-6 py-3 text-[15px] font-bold text-white sm:inline-flex"
                        >
                            <Phone size={16} strokeWidth={2.6} />
                            {contact.phoneDisplay}
                        </a>
                        <button
                            data-testid="nav-menu-toggle"
                            aria-label={open ? "Close menu" : "Open menu"}
                            onClick={() => setOpen((v) => !v)}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/80 text-ink lg:hidden"
                        >
                            {open ? <X size={18} /> : <Menu size={18} />}
                        </button>
                    </div>
                </div>

                <AnimatePresence>
                    {open && (
                        <motion.nav
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            aria-label="Mobile"
                            className="overflow-hidden border-b border-slate-200/80 bg-white/95 backdrop-blur-md lg:hidden"
                        >
                            <div className="flex flex-col gap-1 px-5 py-4">
                                {LINKS.map((link) => (
                                    <button
                                        key={link.label}
                                        data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                                        onClick={() => handleLink(link)}
                                        className="rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-ink/80 transition-colors hover:bg-slate-50 hover:text-ink"
                                    >
                                        {link.label}
                                    </button>
                                ))}
                                <a
                                    data-testid="mobile-nav-call-link"
                                    href={contact.telUrl}
                                    onClick={() => trackClick("call", "navbar-mobile")}
                                    className="btn-gradient mt-2 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold text-white"
                                >
                                    <Phone size={15} strokeWidth={2.6} />
                                    {contact.phoneDisplay}
                                </a>
                            </div>
                        </motion.nav>
                    )}
                </AnimatePresence>
            </motion.header>
        </>
    );
}
